import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IoPlay, IoStop } from 'react-icons/io5';
import { PiMetronomeFill } from 'react-icons/pi';
import { NumberInput } from '@heroui/number-input';
import { ImSpinner2 } from 'react-icons/im';

import Container from '@/components/Container/Container.tsx';
import SquareButton from '@/components/SquareButton/SquareButton.tsx';
import PopoverContainer from '@/components/PopoverContainer/PopoverContainer.tsx';
import SelectList from '@/components/SelectList/SelectList.tsx';
import useAudioPlayer from '@/hooks/useAudioPlayer.ts';
import { NOTE_OPTIONS, SIGNATURE_OPTIONS, NOTES } from '@/core/contants.ts';

import useProgression from '../../hooks/useProgression.ts';

function ProgressionSettings() {
  const { t } = useTranslation();
  const { start, stop, isPlaying, isLoading, bar, beat, bpm, setBpm } = useAudioPlayer();
  const { key, time, setKey, setTime, sequence, metronome, toggleMetronome } = useProgression();

  const handleStart = useCallback(() => {
    start(sequence, time);
  }, [start, sequence, time]);

  const handleMetronome = useCallback(() => {
    stop();
    toggleMetronome();
  }, [toggleMetronome, stop]);

  return (
    <div className="bg-content2">
      <Container className="pb-4">
        <div className="flex flex-wrap gap-2">
          <div className="flex bg-default rounded-xl">
            <SelectList
              label={t('progression.settings.key')}
              options={NOTE_OPTIONS}
              selected={key}
              onChange={(newKey) => {
                stop();
                setKey(newKey);
              }}
            >
              <SquareButton
                className="min-w-14 h-14 sm:min-w-16 sm:h-16"
                subText={t('progression.settings.key')}
                text={NOTES[key].name}
              />
            </SelectList>
            <PopoverContainer
              content={(
                <div className="bg-default-100 rounded-lg">
                  <NumberInput
                    aria-label="bpm"
                    className="max-w-24"
                    maxValue={420}
                    minValue={20}
                    step={1}
                    value={bpm}
                    onValueChange={(newBpm) => {
                      stop();
                      setBpm(newBpm ? newBpm : bpm);
                    }}
                  />
                </div>
              )}
              trigger={(
                <SquareButton
                  className="min-w-14 h-14 sm:min-w-16 sm:h-16"
                  subText={t('progression.settings.bpm')}
                  text={bpm.toString()}
                />
              )}
            />
            <SelectList
              label={t('progression.settings.time')}
              options={SIGNATURE_OPTIONS}
              selected={time}
              onChange={(newTime) => {
                stop();
                setTime(newTime);
              }}
            >
              <SquareButton
                className="min-w-14 h-14 sm:min-w-16 sm:h-16"
                subText={t('progression.settings.time')}
                text={time}
              />
            </SelectList>
            <div className="items-center hidden sm:flex">
              <div className="px-3 min-w-14 h-14 sm:min-w-16 sm:h-16 flex flex-col items-center justify-center border-l-1 border-default-500">
                <div className="text-lg leading-6" id="bar_value">
                  {typeof bar !== 'undefined' ? bar + 1 : '—'}
                </div>
                <div className="text-[0.6rem] leading-4 uppercase">{t('progression.settings.bar')}</div>
              </div>
              <div className="px-3 min-w-14 h-14 sm:min-w-16 sm:h-16 flex flex-col items-center justify-center">
                <div className="text-lg leading-6" id="note_value">
                  {typeof beat !== 'undefined' ? beat + 1 : '—'}
                </div>
                <div className="text-[0.6rem] leading-4 uppercase">{t('progression.settings.beat')}</div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            {isLoading ? (
              <SquareButton
                spinIcon
                color="success"
                icon={ImSpinner2}
              />
            ) : (
              <SquareButton
                color="success"
                icon={isPlaying ? IoStop : IoPlay}
                onPress={isPlaying ? stop : handleStart}
              />
            )}
            <SquareButton
              color={metronome ? 'secondary' : 'default'}
              icon={PiMetronomeFill}
              onPress={handleMetronome}
            />
          </div>
        </div>
      </Container>
    </div>
  );
}

export default ProgressionSettings;
